import React from 'react';
import { 
  ChartBarIcon, 
  UserIcon, 
  MagnifyingGlassIcon, 
  DocumentTextIcon,
  ExclamationCircleIcon,
  AcademicCapIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

// Local bullseye/target icon
const TargetIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
  </svg>
);

const ActivityCard = ({ activity, completed = false, locked = false, compact = false, clickable = false, suppressCta = false }) => {
  // Map of icon names to components
  const iconMap = {
    ChartBarIcon,
    UserIcon,
    SearchIcon: MagnifyingGlassIcon,
    DocumentTextIcon,
    ExclamationCircleIcon,
    AcademicCapIcon,
    ClipboardDocumentIcon,
    CalendarIcon,
    BuildingOfficeIcon,
    CurrencyDollarIcon,
    TargetIcon
  };
  
  // Get the icon component
  const IconComponent = iconMap[activity.icon] || DocumentTextIcon;
  
  const handleActivate = (e) => {
    if (!clickable) return;
    if (locked) { e?.preventDefault?.(); return; }
    if (activity.id === 'find_my_gaps') {
      e?.preventDefault?.();
      try { if (window.ahoy?.track) window.ahoy.track('getting_started_find_my_gaps_clicked'); } catch(_) {}
      
      // If assessment is already complete, redirect to Value Acceleration page
      if (completed) {
        window.location.href = '/succession/value_acceleration';
        return;
      }
      
      // If not complete, redirect to PlanBuilder
      window.location.href = '/succession/plans';
      return;
    }
    const path = activity.path;
    if (!path) return;
    if (activity.id === 'consultation') {
      try { window.open(path, '_blank', 'noopener'); } catch(_) { window.location.href = path; }
    } else {
      window.location.href = path;
    }
  };

  const handleKeyDown = (e) => {
    if (!clickable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate(e);
    }
  };

  return (
    <div className={`bg-succession-bg-tertiary rounded-lg shadow-succession-md overflow-hidden h-full flex flex-col relative ${compact ? '' : 'border border-succession-border'} transition-all duration-300 hover:shadow-succession-lg ${completed ? 'opacity-75' : ''} ${clickable ? 'cursor-pointer hover:shadow-succession-lg' : ''}`}
         style={{ maxWidth: '100%', width: '100%' }}
         role={clickable ? 'button' : undefined}
         tabIndex={clickable ? 0 : undefined}
         onClick={handleActivate}
         onKeyDown={handleKeyDown}>
      {/* Completion indicator */}
      <div className="absolute top-3 right-3 z-10">
        {completed ? (
          <CheckCircleSolidIcon className="h-8 w-8 text-succession-success transition-colors duration-300" aria-hidden="true" />
        ) : (
          <div className="h-8 w-8 rounded-full border-2 border-succession-border transition-colors duration-300"></div>
        )}
      </div>
      
      <div className={`${activity.bgColor} ${compact ? 'p-4' : 'p-6'} flex justify-center transition-colors duration-300 ${locked ? 'opacity-50' : ''}`} style={{ minWidth: 0 }}>
        <IconComponent className={`${compact ? 'h-10 w-10' : 'h-16 w-16'} ${activity.iconColor} transition-colors duration-300`} aria-hidden="true" />
      </div>
      <div className={`${compact ? 'px-4 pt-1 pb-8' : 'p-6'} flex-1 flex flex-col`} style={{ minWidth: 0 }}>
        <h3 className={`${compact ? 'text-xs leading-tight' : 'text-xl'} font-semibold text-succession-text-primary mb-0.5 transition-colors duration-300`}>{activity.title}</h3>
        <p className={`text-succession-text-secondary ${compact ? 'mb-0 text-[10px] leading-snug line-clamp-2' : 'mb-6'} flex-1 transition-colors duration-300`}>{activity.description}</p>
        {suppressCta && compact ? (<div className="mt-auto pt-4" />) : null}
        
        {/* Special handling for consultation activity */}
        {!suppressCta && activity.id === 'consultation' && !completed ? (
          <a 
            href={activity.path} 
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center justify-center ${compact ? 'mt-auto pt-4 px-3 py-2 text-xs' : 'px-4 py-2 text-sm'} border border-succession-border font-medium rounded-md shadow-sm bg-succession-primary hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary w-full transition-all duration-300 text-white`}
          >
            Book Consultation
          </a>
        ) : (
          !suppressCta && (
          <a 
            href={locked ? undefined : (activity.id === 'find_my_gaps' ? undefined : activity.path)}
            onClick={e => { 
              if (locked) { e.preventDefault(); return; }
              if (activity.id === 'find_my_gaps') { 
                e.preventDefault(); 
                try { if (window.ahoy?.track) window.ahoy.track('getting_started_find_my_gaps_clicked'); } catch(_) {}
                
                // If assessment is already complete, redirect to Value Acceleration page
                if (completed) {
                  window.location.href = '/succession/value_acceleration';
                  return;
                }
                
                // If not complete, redirect to PlanBuilder
                window.location.href = '/succession/plans';
                return; 
              }
            }}
            className={`inline-flex items-center justify-center ${compact ? 'mt-auto pt-4 px-3 py-2 text-xs' : 'px-4 py-2 text-sm'} border border-gray-600 font-medium rounded-md shadow-sm ${locked ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : (completed ? 'bg-gray-500 hover:bg-gray-600 text-white' : 'bg-succession-primary hover:bg-succession-primary-hover text-white')} focus:outline-none focus:ring-2 focus:ring-offset-2 ${completed ? 'focus:ring-gray-600' : 'focus:ring-succession-primary'} w-full transition-all duration-300`}
          >
            {locked ? 'Locked' : (completed ? 'View Again' : 'Get Started')}
          </a>
        ))}
      </div>
    </div>
  );
};

export default ActivityCard;
